import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { request } from './api'
import type {
  CommitResponse,
  CreatedImport,
  Expense,
  ExpenseDetail,
  ExpenseList,
  ImportJobResponse,
  MeResponse,
  Report,
  SettleResponse,
  Split,
  SplitListResponse,
  SplitLogEntry,
  Taxonomy,
} from './types'

/**
 * Server state, all of it through TanStack Query.
 *
 * The invalidation lists below are the interesting part. A split touches three
 * things at once -- the split list, the pair's net, and the `/me` summary that
 * shows the same net on the dashboard -- so a mutation that invalidates only the
 * list leaves a stale balance on screen next to a fresh one.
 */

export const keys = {
  me: ['me'] as const,
  accountSync: ['accountSync'] as const,
  categories: (includeArchived: boolean) => ['categories', includeArchived] as const,
  expenses: (period: string, at: string) => ['expenses', period, at] as const,
  expense: (id: string) => ['expense', id] as const,
  report: (period: string, at: string, level: string) => ['report', period, at, level] as const,
  splits: (pairId: string, state: string) => ['splits', pairId, state] as const,
  splitLog: (pairId: string, seq: number) => ['splitLog', pairId, seq] as const,
  importJob: (jobId: string) => ['import', jobId] as const,
}

export function useMe(): UseQueryResult<MeResponse> {
  return useQuery({ queryKey: keys.me, queryFn: () => request<MeResponse>('/me') })
}

interface BootstrapResult {
  profile: MeResponse['profile']
  linkedPairs: string[]
}

export function useBootstrap() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { displayName?: string; baseCurrency?: string }) =>
      request<BootstrapResult>('/me/bootstrap', { method: 'POST', body }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: keys.me })
      // Stands in for the once-per-load sync below, so signing in does not call
      // bootstrap twice in a row.
      qc.setQueryData(keys.accountSync, result)
    },
  })
}

/**
 * Settle the account with the server once per app load.
 *
 * Bootstrap is not only first-login setup: it is also where an invite addressed to
 * this email is redeemed, and where the address is registered so that someone
 * inviting it later is linked at once. Calling it only after the OAuth exchange
 * meant a user who was invited *after* they signed up stayed 待加入 to both sides
 * until their token expired and they happened to log in again -- possibly a month.
 *
 * A query rather than a mutation, purely for the de-duplication: `staleTime:
 * Infinity` makes it run exactly once per load no matter how many screens mount,
 * and the login path seeds the cache so it does not run there at all.
 */
export function useAccountSync(): UseQueryResult<BootstrapResult> {
  const qc = useQueryClient()
  return useQuery({
    queryKey: keys.accountSync,
    queryFn: async () => {
      const result = await request<BootstrapResult>('/me/bootstrap', { method: 'POST', body: {} })
      // A newly linked pair changes the members list and every balance on it.
      if (result.linkedPairs.length > 0) void qc.invalidateQueries({ queryKey: keys.me })
      return result
    },
    staleTime: Infinity,
    gcTime: Infinity,
    // A failure here is not worth a retry storm: the profile either exists, in
    // which case nothing was lost, or `/me` 404s and the dashboard repairs it.
    retry: false,
  })
}

export function useCategories(includeArchived = false): UseQueryResult<Taxonomy> {
  return useQuery({
    queryKey: keys.categories(includeArchived),
    queryFn: async () =>
      (
        await request<{ categories: Taxonomy }>('/categories', {
          query: includeArchived ? { includeArchived: true } : {},
        })
      ).categories,
    // The taxonomy changes rarely and is needed by nearly every screen.
    staleTime: 5 * 60 * 1000,
  })
}

export function useExpenses(period: 'month' | 'year', at: string): UseQueryResult<ExpenseList> {
  return useQuery({
    queryKey: keys.expenses(period, at),
    queryFn: async () => {
      const out = await request<ExpenseList>('/expenses', { query: { period, at } })
      return { expenses: out.expenses, sharedWithMe: out.sharedWithMe ?? [] }
    },
  })
}

export function useExpense(id: string | undefined): UseQueryResult<ExpenseDetail> {
  return useQuery({
    queryKey: keys.expense(id ?? ''),
    enabled: Boolean(id),
    queryFn: () => request<ExpenseDetail>(`/expenses/${id}`),
  })
}

export function useReport(
  period: 'month' | 'year',
  at: string,
  level: 'main' | 'sub',
): UseQueryResult<Report> {
  return useQuery({
    queryKey: keys.report(period, at, level),
    queryFn: async () =>
      (await request<{ report: Report }>('/reports', { query: { period, at, level } })).report,
  })
}

export interface CreateExpenseBody {
  date: string
  amountMinor: number
  currency?: string
  categoryId: string
  subCategoryId?: string
  merchant?: string
  note?: string
  splits?: { pairId: string; shareMinor?: number }[]
}

export function useCreateExpense(): UseMutationResult<Expense, Error, CreateExpenseBody> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: CreateExpenseBody) =>
      (await request<{ expense: Expense }>('/expenses', { method: 'POST', body })).expense,
    onSuccess: (expense) => {
      invalidateLedger(qc, expense.date)
      // A new expense may have created a split, which moves the balance shown on
      // the dashboard and in the members list.
      void qc.invalidateQueries({ queryKey: keys.me })
      void qc.invalidateQueries({ queryKey: ['splits'] })
    },
  })
}

export interface UpdateExpenseBody {
  expectedRevision: number
  date?: string
  amountMinor?: number
  categoryId?: string
  subCategoryId?: string | null
  merchant?: string
  note?: string
}

export function useUpdateExpense(expenseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateExpenseBody) =>
      request<{ expense: Expense; splits: Split[] }>(`/expenses/${expenseId}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: (result, variables) => {
      qc.setQueryData<ExpenseDetail>(keys.expense(expenseId), result)
      invalidateLedger(qc, result.expense.date)
      // A date edit moves the expense between months, so the month it *left*
      // needs invalidating too -- otherwise it lingers in the old list.
      if (variables.date) invalidateLedger(qc, variables.date)
      void qc.invalidateQueries({ queryKey: keys.me })
      void qc.invalidateQueries({ queryKey: ['splits'] })
    },
  })
}

/** Split an expense saved without one (or whose split was cancelled). */
export function useAddExpenseSplit(expenseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { pairId: string; shareMinor?: number; expectedRevision: number }) =>
      request<{ expense: Expense; split: Split }>(`/expenses/${expenseId}/splits`, {
        method: 'POST',
        body,
      }),
    onSuccess: (result) => {
      // Re-read rather than patched in: the detail lists every split the expense
      // has had, and the server is the one that knows that history.
      void qc.invalidateQueries({ queryKey: keys.expense(expenseId) })
      invalidateLedger(qc, result.expense.date)
      void qc.invalidateQueries({ queryKey: keys.me })
      void qc.invalidateQueries({ queryKey: ['splits'] })
    },
  })
}

export function useDeleteExpense(expenseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (expectedRevision: number) =>
      request<void>(`/expenses/${expenseId}`, {
        method: 'DELETE',
        query: { expectedRevision },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expenses'] })
      void qc.invalidateQueries({ queryKey: ['report'] })
      void qc.invalidateQueries({ queryKey: keys.me })
      void qc.invalidateQueries({ queryKey: ['splits'] })
    },
  })
}

function invalidateLedger(qc: ReturnType<typeof useQueryClient>, date: string): void {
  const month = date.slice(0, 7)
  const year = date.slice(0, 4)
  void qc.invalidateQueries({ queryKey: keys.expenses('month', month) })
  void qc.invalidateQueries({ queryKey: keys.expenses('year', year) })
  void qc.invalidateQueries({ queryKey: ['report'] })
}

// ---------------------------------------------------------------------------
// Categories (requirement 9)
// ---------------------------------------------------------------------------

export function useCreateCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { label: string; parentId?: string; color?: string; icon?: string }) =>
      request<{ categoryId: string; label: string }>('/categories', { method: 'POST', body }),
    onSuccess: () => invalidateTaxonomy(qc),
  })
}

export function useUpdateCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: {
      id: string
      body: { label?: string; color?: string; icon?: string; archived?: boolean }
    }) =>
      request<{ categories: Taxonomy }>(`/categories/${encodeURIComponent(args.id)}`, {
        method: 'PATCH',
        body: args.body,
      }),
    onSuccess: () => invalidateTaxonomy(qc),
  })
}

export function useDeleteCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { id: string; reassignTo?: string }) =>
      request<{ archived: boolean; reassigned: number }>(
        `/categories/${encodeURIComponent(args.id)}`,
        { method: 'DELETE', query: { reassignTo: args.reassignTo } },
      ),
    onSuccess: () => invalidateTaxonomy(qc),
  })
}

function invalidateTaxonomy(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['categories'] })
  // Labels are an overlay, so a rename changes every report immediately even
  // though not one expense was touched (design SS8.2).
  void qc.invalidateQueries({ queryKey: ['report'] })
}

// ---------------------------------------------------------------------------
// Pairs and splits (requirements 5 and 6)
// ---------------------------------------------------------------------------

export interface InviteResult {
  pairId: string
  displayName: string
  /** `linked` when that address already had an account, so there is nothing to wait for. */
  status: 'pending' | 'linked'
  currency: string
  /** The person was already on the list; no second pair was created. */
  alreadyExisted: boolean
}

export function useInviteMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; displayName: string }) =>
      request<InviteResult>('/contacts', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  })
}

/**
 * Remove a split member (requirement 5).
 *
 * The server refuses while anything is owed or unsettled, so there is no
 * expected-value argument to send: the rule is a DynamoDB condition, not
 * something the client asserts. A 409 here means "settle first", and its detail
 * carries the current balance -- which is why the members list and every split
 * list are invalidated even on failure.
 */
export function useRemoveMember(pairId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      request<{ pairId: string; closedAt: string }>(`/pairs/${pairId}`, { method: 'DELETE' }),
    onSettled: () => invalidatePair(qc, pairId),
  })
}

export function useSplits(
  pairId: string | undefined,
  state: 'open' | 'all',
): UseQueryResult<SplitListResponse> {
  return useQuery({
    queryKey: keys.splits(pairId ?? '', state),
    enabled: Boolean(pairId),
    queryFn: () => request<SplitListResponse>(`/pairs/${pairId}/splits`, { query: { state } }),
  })
}

export function useSplitLog(pairId: string, seq: number | undefined) {
  return useQuery({
    queryKey: keys.splitLog(pairId, seq ?? 0),
    enabled: seq !== undefined,
    queryFn: async () =>
      (await request<{ entries: SplitLogEntry[] }>(`/pairs/${pairId}/splits/${seq}/log`)).entries,
  })
}

export function useUpdateSplit(pairId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { seq: number; shareMinor: number; expectedRevision: number }) =>
      request<{ split: Split; netMinor: number }>(`/pairs/${pairId}/splits/${args.seq}`, {
        method: 'PATCH',
        body: { shareMinor: args.shareMinor, expectedRevision: args.expectedRevision },
      }),
    onSuccess: () => invalidatePair(qc, pairId),
  })
}

export function useRemoveSplit(pairId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { seq: number; expectedRevision: number }) =>
      request<{ netMinor: number }>(`/pairs/${pairId}/splits/${args.seq}`, {
        method: 'DELETE',
        query: { expectedRevision: args.expectedRevision },
      }),
    onSuccess: () => invalidatePair(qc, pairId),
  })
}

/**
 * Settle everything outstanding.
 *
 * Both expected values come from the list response and go back unmodified; the
 * server's condition is what actually prevents settling an amount that changed
 * while the screen was open (design SS5.7), so the client must not recompute
 * them from anything it derived itself.
 */
export function useSettle(pairId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { expectedNetMinor: number; expectedNextSeq: number }) =>
      request<SettleResponse>(`/pairs/${pairId}/settle`, { method: 'POST', body: args }),
    onSuccess: () => invalidatePair(qc, pairId),
  })
}

export function useMarkSeen(pairId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => request<void>(`/pairs/${pairId}/seen`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['splits', pairId] }),
  })
}

function invalidatePair(qc: ReturnType<typeof useQueryClient>, pairId: string): void {
  void qc.invalidateQueries({ queryKey: ['splits', pairId] })
  void qc.invalidateQueries({ queryKey: ['splitLog', pairId] })
  // The same balance appears on the dashboard and the members list.
  void qc.invalidateQueries({ queryKey: keys.me })
  // And a share is spending on both sides: the payer's totals count what is
  // left after it, the other member's count the share itself.
  void qc.invalidateQueries({ queryKey: ['expenses'] })
  void qc.invalidateQueries({ queryKey: ['report'] })
}

// ---------------------------------------------------------------------------
// Import (requirement 4)
// ---------------------------------------------------------------------------

export function useCreateImport() {
  return useMutation({
    mutationFn: () => request<CreatedImport>('/imports', { method: 'POST' }),
  })
}

/**
 * Poll a job while it is parsing.
 *
 * The parse runs in a separate Lambda triggered by the upload, so the only way
 * the client learns it finished is by asking. Polling stops the moment the job
 * reaches a terminal state, so a failed parse does not poll forever.
 */
export function useImportJob(
  jobId: string | undefined,
  opts: { poll?: boolean } = {},
): UseQueryResult<ImportJobResponse> {
  return useQuery({
    queryKey: keys.importJob(jobId ?? ''),
    enabled: Boolean(jobId),
    queryFn: () => request<ImportJobResponse>(`/imports/${jobId}`),
    refetchInterval: (query) => {
      if (!opts.poll) return false
      const status = query.state.data?.job.status
      return status === 'PENDING' || status === 'PARSING' ? 1500 : false
    },
  })
}

export function usePatchImportRows(jobId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (rows: {
      rowIndex: number
      categoryId?: string
      subCategoryId?: string | null
      include?: boolean
      splitPlan?: { pairId: string; shareMinor?: number } | null
      note?: string | null
    }[]) =>
      request<{ rows: ImportJobResponse['rows'] }>(`/imports/${jobId}/rows`, {
        method: 'PATCH',
        body: { rows },
      }),
    onSuccess: (result) => {
      qc.setQueryData(keys.importJob(jobId), (prev: ImportJobResponse | undefined) =>
        prev ? { ...prev, rows: result.rows } : prev,
      )
    },
  })
}

export function useCommitImport(jobId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => request<CommitResponse>(`/imports/${jobId}/commit`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expenses'] })
      void qc.invalidateQueries({ queryKey: ['report'] })
      void qc.invalidateQueries({ queryKey: keys.importJob(jobId) })
      void qc.invalidateQueries({ queryKey: keys.me })
      void qc.invalidateQueries({ queryKey: ['splits'] })
    },
  })
}
